import { isDiscontinued } from '@/lib/app-categories/discontinued'
import { TimeZone } from '@/lib/constants/time-zones'
import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'
import {
  Recommendation,
  Classification,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type { Block, ResultType } from '@/types/app'
import type { App, Category, AppCategoryRecommendation } from '@/payload-types'

const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        'text-10',
        'text-12',
        'text-13',
        'text-14',
        'text-15',
        'text-16',
        'text-17',
        'text-18',
        'text-20',
        'text-24',
        'text-36',
        'text-48',
        'text-64',
      ],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs))
}

export function capitalize(str: string): string {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// Type guard for Payload relationships that may be populated objects or raw IDs
export function isPopulatedObject<T extends { id: number }>(
  value: T | T['id'] | null | undefined,
): value is T {
  return typeof value === 'object' && value !== null && 'id' in value
}

export function toSlug(str: string): string {
  if (!str) return ''
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function fromSlug(str: string) {
  if (!str) return ''
  return str.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

export function trimUrl(url: string) {
  if (!url) return ''
  return url.replace(/^(https?:\/\/)?(www\.)?/, '')
}

function animateScrollTo(getTargetY: () => number, onComplete: () => void, duration = 400): void {
  const startY = window.scrollY
  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
  let startTime: number | null = null

  const frame = (currentTime: number) => {
    if (startTime === null) startTime = currentTime
    const progress = Math.min((currentTime - startTime) / duration, 1)
    const ease = easeOutCubic(progress)
    const distance = getTargetY() - startY

    window.scrollTo(0, startY + distance * ease)

    if (progress < 1) {
      requestAnimationFrame(frame)
    } else {
      onComplete()
    }
  }

  requestAnimationFrame(frame)
}

type ResponsiveOffset = number | { mobile: number; desktop: number }

type ScrollToSectionOptions = {
  id: string
  offset: ResponsiveOffset
  animate?: boolean
  hideHash?: boolean
}

function resolveOffset(offset: ResponsiveOffset): number {
  if (typeof offset === 'number') return offset
  return window.innerWidth < 768 ? offset.mobile : offset.desktop
}

// Next.js App Router patches the window.history.* instance methods (see
// app-router.tsx) to keep usePathname/useSearchParams in sync, but doing so
// dispatches an internal ACTION_RESTORE on every call, which can trigger the
// router's own scrollIntoView for the new hash — fighting our intentional
// scroll offset and causing visible jank when the hash updates frequently
// (e.g. scroll-spy). Next only ever reassigns the instance property, never
// History.prototype, so calling the prototype method directly stays native.
function nativeReplaceState(url: string): void {
  History.prototype.replaceState.call(window.history, window.history.state, '', url)
}

function clearHash(): void {
  if (!window.location.hash) return
  const url = new URL(window.location.href)
  url.hash = ''
  nativeReplaceState(url.toString())
}

// Keeps the hash following scroll-driven active-section changes without pushing
// history entries (unlike clicking a link, which intentionally adds one).
// export function replaceHash(id: string): void {
//   if (window.location.hash === `#${id}`) return
//   nativeReplaceState(`#${id}`)
// }

export function scrollToSection({
  id,
  offset,
  animate = false,
  hideHash = false,
}: ScrollToSectionOptions): Promise<void> {
  return new Promise((resolve) => {
    if (id === 'top') {
      if (animate) {
        animateScrollTo(
          () => 0,
          () => {
            clearHash()
            resolve()
          },
        )
      } else {
        window.scrollTo({ top: 0 })
        clearHash()
        resolve()
      }
      return
    }

    let element = document.getElementById(id)
    if (!element) {
      resolve()
      return
    }

    const hiddenContent = element.closest<HTMLElement>('[data-section-content-visible="false"]')
    if (hiddenContent) {
      const visibleParent = hiddenContent.parentElement?.closest<HTMLElement>('[data-section-id]')
      if (visibleParent) {
        element = visibleParent
      }
    }

    // If this is a child section, also check if parent is expanded
    if (element.dataset.sectionChild === 'true') {
      const parentSection = element.parentElement?.closest('[data-section-parent="true"]')
      if (parentSection && (parentSection as HTMLElement).dataset.expanded === 'false') {
        resolve()
        return
      }
    }

    const resolvedOffset = resolveOffset(offset)

    if (animate) {
      animateScrollTo(
        () => element.getBoundingClientRect().top + window.scrollY - resolvedOffset,
        () => {
          if (hideHash) {
            clearHash()
          } else {
            nativeReplaceState(`#${id}`)
          }
          resolve()
        },
      )
    } else {
      const targetY = element.getBoundingClientRect().top + window.scrollY - resolvedOffset
      window.scrollTo({ top: targetY, behavior: 'auto' })
      if (hideHash) {
        clearHash()
      } else {
        nativeReplaceState(`#${id}`)
      }
      resolve()
    }
  })
}

export const checkScroll = (
  refs: React.RefObject<(HTMLElement | null)[]>,
  setActive: (value: string | null) => void,
  offset: number = 190,
) => {
  let activeSet = false

  refs.current.forEach((ref) => {
    if (ref) {
      const refRect = ref.getBoundingClientRect()

      if (refRect.top <= offset) {
        setActive(ref.getAttribute('data-name'))
        activeSet = true
      }
    }
  })

  if (!activeSet) {
    setActive(null)
  }
}

const timeZone = TimeZone.Central

export function formatUpdatedDate(date?: string) {
  const dateObj = date ? new Date(date) : new Date()

  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone,
  })

  return formatter.format(dateObj)
}

// Full ISO 8601 datetime with timezone offset (e.g. 2025-12-08T00:00:00-06:00) in
// the same timezone as formatUpdatedDate. Use for all JSON-LD dates (Article
// datePublished/dateModified, VideoObject uploadDate). The date portion matches the
// visible date, and Google's Rich Results Test requires the time + timezone offset
// (date-only values are flagged on VideoObject).
export function formatIsoDateTime(date?: string) {
  const dateObj = date ? new Date(date) : new Date()

  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone,
    timeZoneName: 'longOffset',
  }).formatToParts(dateObj)

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? ''

  // longOffset yields e.g. "GMT-06:00"; strip the prefix, fall back to UTC
  const offset = get('timeZoneName').replace('GMT', '') || '+00:00'

  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}${offset}`
}

export function hexToRgba(hex: string, alpha = 1) {
  if (!hex) {
    console.error('No hex provided to hexToRgba function')
    return 'rgba(0, 0, 0, 1)'
  }

  hex = hex.replace(/^#/, '')

  let r, g, b
  if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16)
    g = parseInt(hex.slice(2, 4), 16)
    b = parseInt(hex.slice(4, 6), 16)
  } else {
    throw new Error('Invalid hex color format')
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function formatNumber(value: number): string {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export function getBrandColors(
  app?: Pick<
    App,
    'brandPrimaryColor' | 'brandSecondaryColor' | 'brandTertiaryColor' | 'brandBackgroundColor'
  > | null,
): {
  primaryColor: string
  secondaryColor: string
  tertiaryColor: string
  backgroundColor: string
} {
  const defaultColors = {
    brandPrimaryColor: '#000000',
    brandSecondaryColor: '#000000',
    brandTertiaryColor: '#000000',
    brandBackgroundColor: '#FFFFFF',
  }

  const { brandPrimaryColor, brandSecondaryColor, brandTertiaryColor, brandBackgroundColor } =
    app || defaultColors

  return {
    primaryColor: brandPrimaryColor || brandBackgroundColor || defaultColors.brandPrimaryColor,
    secondaryColor:
      brandSecondaryColor || brandBackgroundColor || defaultColors.brandSecondaryColor,
    tertiaryColor: brandTertiaryColor || brandBackgroundColor || defaultColors.brandTertiaryColor,
    backgroundColor: brandBackgroundColor || defaultColors.brandBackgroundColor,
  }
}

export function sortToFeaturedOrder(items: any[], fallback: number = 5) {
  const sortedItems = items
    .filter((item) => item.isFeatured)
    .sort((a, b) => a.featuredOrder - b.featuredOrder)

  if (sortedItems.length === 0) {
    return items.slice(0, fallback)
  }

  return sortedItems
}

export function delayedHover<T>(setState: (value: T) => void, getValue: T, delay: number = 150) {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return {
    onMouseEnter: () => {
      timeout = setTimeout(() => {
        setState(getValue)
        timeout = null
      }, delay)
    },
    onMouseLeave: () => {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      setState(null as unknown as T)
    },
    onClick: () => {
      setState(getValue)
    },
  }
}

export function getRelativeTime(timestamp: Date | string): string {
  const now = new Date()
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)

  if (isNaN(date.getTime())) {
    return 'just now'
  }

  const rawDiff = now.getTime() - date.getTime()
  const diffInSeconds = Math.floor(Math.max(0, rawDiff) / 1000)

  if (diffInSeconds < 60) {
    return `${diffInSeconds} ${diffInSeconds === 1 ? 'second' : 'seconds'} ago`
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 30) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`
  }

  const diffInMonths = Math.floor(diffInDays / 30)
  return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`
}

export function determineEfficiencyLabels(rating?: number) {
  switch (rating) {
    case 1:
      return {
        tier: 'is deficient',
        description: 'Fails to meet core needs. Unreliable or broken experience.',
      }
    case 2:
      return {
        tier: 'has limited capability',
        description: 'Basic functionality exists, but major friction or gaps hold it back.',
      }
    case 3:
      return {
        tier: 'is capable',
        description: 'Gets the job done in parts, but lacks consistency or smoothness.',
      }
    case 4:
      return {
        tier: 'is effective',
        description: 'Generally usable with some polish, but still falls short of true efficiency.',
      }
    case 5:
      return {
        tier: 'is proficient',
        description: 'Reliable, smooth, and productive. Meets the standard for efficient tools.',
      }
    case 6:
      return {
        tier: 'is highly efficient',
        description: 'Fast, intuitive, and workflow-friendly. Clearly saves time and effort.',
      }
    case 7:
      return {
        tier: 'is exceptional',
        description: 'Best-in-class. Elegant, powerful, and designed for peak productivity.',
      }
    default:
      return {
        tier: 'efficiency score',
        description: 'An efficiency point is earned for each criterion scoring 5/7 or higher.',
      }
  }
}

export function extractPrimaryCategory(app: Pick<App, 'categoryRecommendations'>) {
  const appRecs = app.categoryRecommendations?.docs as AppCategoryRecommendation[]
  const primaryCategory = appRecs?.find((rec) => rec.classification === 'primary')
    ?.category as Category
  return primaryCategory?.name
}

export function removeMentions(mention: string) {
  if (!mention) return ''

  const result = mention
    // First, convert "@alias Some" to "Some" by capturing the following chunk
    .replace(/@\S+\s+([^\s].*?)(?=$|\s@|\s$)/g, (_m, following) => following)
    // Then remove any remaining standalone "@alias" tokens
    .replace(/@\S+/g, '')

  // Collapse excessive whitespace introduced by removals
  return result.trim().replace(/\s{2,}/g, ' ')
}

// Centralized config for result types - single source of truth for slugs and icons
const resultTypeConfig: Record<ResultType, { slug: string; icon: string }> = {
  search: { slug: 'search', icon: 'magnifier-small' },
  chat: { slug: 'chat', icon: 'chat' },
  review: { slug: 'apps', icon: 'review' },
  deal: { slug: 'deals', icon: 'deal' },
  stack: { slug: 'stacks', icon: 'stack' },
  comparison: { slug: 'compare', icon: 'comparison' },
  alternative: { slug: 'alternatives', icon: 'alternative' },
  app: { slug: 'apps', icon: 'magnifier-small' },
  best: { slug: 'best', icon: 'best' },
  blog: { slug: 'blog', icon: 'pen-writing-4' },
  course: { slug: 'courses', icon: 'graduation-cap' },
  page: { slug: '', icon: 'file' }, // Pages are at root level: /{slug}
}

// Derived maps for backwards compatibility
export const slugMap: Record<ResultType, string> = Object.fromEntries(
  Object.entries(resultTypeConfig).map(([key, value]) => [key, value.slug]),
) as Record<ResultType, string>

export const iconMap: Record<ResultType, string> = Object.fromEntries(
  Object.entries(resultTypeConfig).map(([key, value]) => [key, value.icon]),
) as Record<ResultType, string>

export const labelMap: Record<string, string> = {
  comparison: 'compare',
}

export const getLabel = (key: string): string => {
  return labelMap[key] ?? key
}

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }
    return map[char] ?? char
  })
}

// Helper to convert Lexical content to a string for page title blocks
export function convertLexicalToString(children: Block[], theme?: 'light' | 'dark' | null): string {
  const acceptedTypes = ['paragraph', 'heading']

  return children
    .map((child) => {
      if (!child) return ''
      // Handle paragraph blocks that contain text nodes
      if (acceptedTypes.includes(child.type) && child.children) {
        return child.children
          .map((textNode: any) => {
            if (!textNode) return ''
            if (textNode.type === 'text') {
              const text = escapeHtml(textNode.text || '')
              const format = textNode.format || 0

              if (format === 1) {
                const className = theme === 'dark' ? ' class="dark"' : ''
                return `<span${className}>${text}</span>`
              }
              return text
            }
            return ''
          })
          .join('')
      }

      // Handle direct text nodes (fallback)
      if (child.type === 'text' || (child as any).text) {
        const text = escapeHtml((child as any).text || '')
        const format = (child as any).format || 0

        if (format === 1) {
          const className = theme === 'dark' ? ' class="dark"' : ''
          return `<span${className}>${text}</span>`
        }
        return text
      }

      return ''
    })
    .join('')
}

export function hasRichTextContent(children: Block[] | undefined): boolean {
  if (!children?.length) return false
  return children.some(
    (block) =>
      (typeof block.text === 'string' && block.text.trim().length > 0) ||
      ('children' in block &&
        Array.isArray(block.children) &&
        hasRichTextContent(block.children as Block[])),
  )
}

export function timeToSeconds(timestamp: string | null | undefined): number {
  // Defensive: a populate that omits the timestamp field, or a missing optional
  // value (e.g. video_apps_app rows with no timestampEnd), would otherwise crash
  // the static build with "Cannot read properties of undefined (reading 'trim')".
  if (!timestamp) return 0
  const parts = timestamp.trim().split(':')
  if (parts.length !== 2 && parts.length !== 3) return 0

  const nums = parts.map((part) => Number(part))
  if (nums.some((n) => !Number.isFinite(n))) return 0

  if (nums.length === 3) return nums[0] * 3600 + nums[1] * 60 + nums[2]
  if (nums.length === 2) return nums[0] * 60 + nums[1]

  return 0
}

export function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return m > 0 && s > 0 ? `${m}m ${s}s` : m > 0 ? `${m}m` : `${s}s`
}

export function hapticTap(duration: number = 10) {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(duration)
  }
}

export function filterGoodCategories(
  appCategoryRecommendations: AppCategoryRecommendation[],
): Array<
  Category & {
    recommendation: Recommendation
    classification: Classification
  }
> {
  const goodCategories = appCategoryRecommendations
    .filter(
      (r) =>
        !isDiscontinued(r) &&
        (r.recommendation === Recommendation.TopPick ||
          r.classification === Classification.Primary) &&
        isPopulatedObject<Category>(r.category),
    )
    .map((r) => {
      return {
        ...(r.category as Category),
        recommendation: r.recommendation,
        classification: r.classification,
      }
    }) as (Category & {
    recommendation: Recommendation
    classification: Classification
  })[]

  return goodCategories
}

export function getReviewAwardCategory(
  app: Pick<App, 'categoryRecommendations'>,
): ReturnType<typeof filterGoodCategories>[number] | undefined {
  return filterGoodCategories(
    (app.categoryRecommendations?.docs ?? []).filter(isPopulatedObject<AppCategoryRecommendation>),
  ).find((category) => category.classification === Classification.Primary)
}

export const getOverlappingAppCategory = (apps: App[]): Category | undefined => {
  const requiredOverlapCount = new Set(apps.map((app) => app.id)).size
  if (requiredOverlapCount < 2) return undefined

  const categoryMap = new Map<
    Category['id'],
    { category: Category; appIds: Set<App['id']>; isPrimary: boolean }
  >()

  apps.forEach((app) => {
    const acrs = (app.categoryRecommendations?.docs ?? []) as AppCategoryRecommendation[]

    acrs.forEach((acr) => {
      if (!isPopulatedObject<Category>(acr.category)) return

      const category = acr.category as Category
      const categoryId = category.id
      const isPrimary = acr.classification === Classification.Primary

      if (!categoryMap.has(categoryId)) {
        categoryMap.set(categoryId, { category, appIds: new Set(), isPrimary: false })
      }

      const entry = categoryMap.get(categoryId)!
      entry.appIds.add(app.id)
      if (isPrimary) {
        entry.isPrimary = true
      }
    })
  })

  // Find overlapping categories (categories with more than one app)
  const overlappingCategories = Array.from(categoryMap.values()).filter(
    ({ appIds }) => appIds.size === requiredOverlapCount,
  )

  // Return the primary overlapping category, or the first overlapping category if no primary exists
  const primaryCategory = overlappingCategories.find(({ isPrimary }) => isPrimary)
  return primaryCategory?.category ?? overlappingCategories[0]?.category
}

type AppRelationItem = {
  app: App['id'] | Pick<App, 'id'> | null | undefined
}

export function filterFromAppIds({
  apps,
  array,
}: {
  apps: App[]
  array: AppRelationItem[] | undefined
}) {
  if (!array) return null

  const orderedAppIds = new Set(apps.map((app) => app.id))
  const filteredResults = array.filter((item) => {
    const appId =
      typeof item.app === 'number'
        ? item.app
        : isPopulatedObject<Pick<App, 'id'>>(item.app)
          ? item.app.id
          : null

    if (appId === null) return false
    return orderedAppIds.has(appId)
  })

  return filteredResults
}
